import React from 'react';
import { Shield, Check, X } from 'lucide-react';
import RoleBadge from '../../components/RoleBadge';

const RolesPermissions = () => {
  // Define permissions based on Firestore rules
  const permissions = [
    {
      category: 'Dashboard & Core',
      items: [
        { name: 'View Dashboard', admin: true, sales: true, technician: true, marketing: true },
        { name: 'Access Calculator', admin: true, sales: true, technician: false, marketing: false },
        { name: 'View Pipeline', admin: true, sales: true, technician: false, marketing: false },
      ],
    },
    {
      category: 'Leads & Clients',
      items: [
        { name: 'View Leads', admin: true, sales: true, technician: false, marketing: true },
        { name: 'Create/Edit Leads', admin: true, sales: true, technician: false, marketing: false },
        { name: 'View Clients', admin: true, sales: true, technician: true, marketing: false },
        { name: 'Create/Edit Clients', admin: true, sales: true, technician: false, marketing: false },
      ],
    },
    {
      category: 'Tasks & Communication',
      items: [
        { name: 'View All Tasks', admin: true, sales: true, technician: true, marketing: true },
        { name: 'Create Tasks', admin: true, sales: true, technician: true, marketing: true },
        { name: 'Edit All Tasks', admin: true, sales: true, technician: false, marketing: false },
        { name: 'Edit Assigned Tasks Only', admin: false, sales: false, technician: true, marketing: true },
        { name: 'Access Inbox', admin: true, sales: true, technician: false, marketing: false },
      ],
    },
    {
      category: 'Settings & Configuration',
      items: [
        { name: 'Access Settings', admin: true, sales: false, technician: false, marketing: false },
        { name: 'Manage Team Members', admin: true, sales: false, technician: false, marketing: false },
        { name: 'Configure Pricing', admin: true, sales: false, technician: false, marketing: false },
        { name: 'Manage Pipeline Stages', admin: true, sales: false, technician: false, marketing: false },
        { name: 'Configure Automations', admin: true, sales: false, technician: false, marketing: false },
        { name: 'Manage Integrations', admin: true, sales: false, technician: false, marketing: false },
      ],
    },
    {
      category: 'Quotes & Documents',
      items: [
        { name: 'Create Quotes', admin: true, sales: true, technician: false, marketing: false },
        { name: 'View Quotes', admin: true, sales: true, technician: false, marketing: false },
        { name: 'Edit Quotes', admin: true, sales: true, technician: false, marketing: false },
      ],
    },
  ];

  const PermissionIcon = ({ allowed }) => {
    if (allowed) {
      return <Check className="h-5 w-5 text-green-600" />;
    }
    return <X className="h-5 w-5 text-gray-300" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Shield className="h-6 w-6 text-primary" />
        <div>
          <h2 className="text-2xl font-bold text-secondary">Roles & Permissions</h2>
          <p className="text-sm text-muted-foreground">
            View access permissions for each role in your organization.
          </p>
        </div>
      </div>

      {/* Role Definitions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Role Definitions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-2">
            <RoleBadge role="admin" />
            <p className="text-sm text-gray-600">
              Full access to all features, settings, and team management.
            </p>
          </div>
          <div className="space-y-2">
            <RoleBadge role="sales" />
            <p className="text-sm text-gray-600">
              Access to leads, clients, pipeline, calculator, and quotes.
            </p>
          </div>
          <div className="space-y-2">
            <RoleBadge role="technician" />
            <p className="text-sm text-gray-600">
              View clients, manage assigned tasks, and track field work.
            </p>
          </div>
          <div className="space-y-2">
            <RoleBadge role="marketing" />
            <p className="text-sm text-gray-600">
              View leads for campaign tracking and manage marketing tasks.
            </p>
          </div>
        </div>
      </div>

      {/* Permissions Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {permissions.map((section, sectionIdx) => (
          <div key={sectionIdx}>
            {/* Section Header */}
            <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                {section.category}
              </h4>
            </div>

            {/* Permissions Table */}
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Permission
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Admin
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sales
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Technician
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Marketing
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {section.items.map((permission, permIdx) => (
                  <tr key={permIdx} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {permission.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex justify-center">
                        <PermissionIcon allowed={permission.admin} />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex justify-center">
                        <PermissionIcon allowed={permission.sales} />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex justify-center">
                        <PermissionIcon allowed={permission.technician} />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex justify-center">
                        <PermissionIcon allowed={permission.marketing} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Security Note</p>
            <p>
              These permissions are enforced at the database level through Firestore security rules.
              Role assignments can only be modified by administrators through the Team Members page.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RolesPermissions;
